import { LightIcon } from "@xola/icons";
import { Alert, flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { Form, ProductObject } from "types";
import { ConfirmLeaveModal } from "../../Common/ConfirmLeaveModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { Questions } from "./Questions";
import { MEMBERSHIP_QUESTIONNAIRE_REQUIRED_TOOLTIP } from "@/components/Product/Questionnaire/ProductQuestionnaire";
import {
    getOptionalSourcesList,
    getSavedViewsForUpdate,
    useDeleteForm,
    useForm,
    useQuestionnairePreference,
    useSaveForm,
} from "@/hooks/questionnaireFormHooks";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { createNewForm, getFormPayload, isQuestionnaireSkipped } from "@/models/Form/Form";

interface GlobalQuestionnaireProps {
    productObject?: ProductObject;
}

export const GlobalQuestionnaire = ({ productObject }: GlobalQuestionnaireProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const [originalForm, setOriginalForm] = useState<Form>();
    const { mutateAsync: saveOrUpdateForm, isLoading: updating } = useSaveForm();
    const { mutateAsync: deleteForm, isLoading: deleting } = useDeleteForm(originalForm);

    const { form, setForm, isLoading, initialForm } = useForm(undefined, productObject);
    const { viewConfigurations, setPreference } = useQuestionnairePreference();
    const [isGlobalQuestionnaireEnabled, setIsGlobalQuestionnaireEnabled] = useState(
        isGlobalQuestionnairePresent(form, productObject),
    );
    const [formChanged, setFormChanged] = useState(false);

    useEffect(() => {
        setFormChanged(!isEqual(initialForm, form));
        setIsGlobalQuestionnaireEnabled(isGlobalQuestionnairePresent(form, productObject));
    }, [form, initialForm, productObject]);

    const isMembership = productObject === ProductObject.MEMBERSHIP;

    if (isLoading) {
        return <Loader />;
    }

    if (isMembership && !form) {
        // Membership must always have a global form
        return (
            <ContentLayout isWide>
                <h2 className="text-xl font-bold">Questionnaire</h2>
                <Alert color="danger" className="mt-4">
                    There was some error configuring Memberships. Please contact support.
                </Alert>
            </ContentLayout>
        );
    }

    const handleSaveQuestionnaireForSavedViews = async (form: Form) => {
        if (!viewConfigurations) {
            return;
        }

        const preferencesToUpdate = getSavedViewsForUpdate(form, viewConfigurations, "global_questionnaire");
        setPreference((preference) => {
            preference.value.configurations = [...preferencesToUpdate];
        });
    };

    const handleGlobalQuestionnaireChange = (isEnabled: boolean) => {
        if (isEnabled) {
            const newForm = originalForm ?? createNewForm(seller, false, undefined, productObject);
            setForm(newForm);
        } else {
            setOriginalForm(form);
            setForm(undefined);
        }
    };

    const handleCancelClick = () => setForm(initialForm);

    const handleSkipRequiredFields = (value: boolean) => {
        if (form) {
            setForm({ ...form, optionalSourcesList: getOptionalSourcesList(form, value) });
        }
    };

    const handleSave = async () => {
        if (form) {
            await saveOrUpdateForm(getFormPayload(form));
            await handleSaveQuestionnaireForSavedViews(form);
        } else {
            await deleteForm();
            setOriginalForm(undefined);
        }

        invalidate("forms");
        flash.success("Successfully updated Questionnaire");
    };

    return (
        <ContentLayout isWide>
            <ConfirmLeaveModal isFormTouched={formChanged} />
            <h2 className="text-xl font-bold">Questionnaire</h2>
            <Alert className="mt-12" color="primary">
                <div className="space-y-1 p-2 leading-lg text-gray-darker">
                    <Switch.Group>
                        <Tooltip content={MEMBERSHIP_QUESTIONNAIRE_REQUIRED_TOOLTIP} disabled={!isMembership}>
                            <Switch
                                disabled={isMembership}
                                isChecked={isGlobalQuestionnaireEnabled}
                                onChange={handleGlobalQuestionnaireChange}
                            />
                        </Tooltip>

                        <Switch.Label direction="right">
                            <div className="space-x-2 font-bold">
                                <LightIcon />
                                <span>
                                    {isGlobalQuestionnaireEnabled ? "Enabled" : "Disabled"} global questionnaire
                                </span>
                            </div>
                        </Switch.Label>
                    </Switch.Group>
                    <p>
                        Global customer questionnaire will automatically apply to all of your {productObject}. You may
                        override the global customer questionnaire at the {productObject} level.
                    </p>
                </div>
            </Alert>
            {form && (
                <>
                    <ContentLayout.Section title="Questionnaire Display">
                        <div className="flex flex-1 justify-between pb-2 pt-1 pl-4">
                            <div className="flex-1 items-center justify-between space-y-2 pl-4">
                                <Switch.Group>
                                    <Switch
                                        isChecked={isQuestionnaireSkipped(form)}
                                        onChange={handleSkipRequiredFields}
                                    />
                                    <Switch.Label direction="right">
                                        Skip required field validations for back office purchases
                                    </Switch.Label>
                                </Switch.Group>
                            </div>
                        </div>
                    </ContentLayout.Section>
                    <ContentLayout.Section
                        title="Questions"
                        description="Add available questions into the questionnaire and/or create your own question."
                    >
                        <Questions
                            isAttendeeUnique
                            form={form}
                            isMembershipProduct={isMembership}
                            onFormChange={setForm}
                        />
                    </ContentLayout.Section>
                </>
            )}

            <ContentLayout.Footer preferenceSidebar>
                <SubmitButton disabled={updating || deleting} onClick={handleSave}>
                    Save
                </SubmitButton>

                <SecondaryButton type="reset" onClick={handleCancelClick}>
                    Cancel
                </SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};

const isGlobalQuestionnairePresent = (form: Form | undefined, productObject: ProductObject | undefined): boolean => {
    if (!form) {
        return false;
    }

    if (productObject === undefined) {
        return true;
    }

    return form.productType === productObject;
};
